<template>
  <Table
    item-type="Payment"
    date-key="date"
    :items="installments"
    :total-item-count="installments.length"
    :headers="tableHeaders"
    :state="state"
    :count="{ singular: 'payment', plural: 'payments' }"
    hide-header
  >
    <template #tableHeader>
      <div class="table-header mb-2 flex justify-between items-center">
        <h3 class="text-lg font-bold">
          Payments
        </h3>

        <RoleControlledAction
          v-slot="{ restricted }"
          :user-role="roleName"
          :config-object="roleConfig.editSchedule"
        >
          <router-link
            v-if="canEditPayments"
            :class="{ 'custom-disabled': restricted }"
            :to="restricted ? '' : {
              name: Pages.editPaymentSchedule,
              params: {
                projectId,
                id: orderId,
              },
            }"
            class="btn btn-primary btn-sm"
          >
            Edit Payments
          </router-link>
        </RoleControlledAction>
      </div>
    </template>

    <template #row(id)="{ item }">
      <div class="truncate w-36">
        <routerLink :to="{ name: Pages.dashboardPaymentPlan, params: { id: item.id } }">
          <span>{{ item.id }}</span>
        </routerLink>
      </div>
    </template>

    <template #row(status)="{ item }">
      <Tooltip v-if="(item.status === 'Failed' || item.status === 'Past Due') && item.failureReason">
        <template #trigger="{ assignReference, show, hide }">
          <div
            :ref="(el: any) => assignReference(el)"
            @mouseenter="show"
            @mouseleave="hide"
          >
            <div
              class="inline-block w-2 h-2 mr-1 rounded-full"
              :class="paymentStatusToColorMap[item.status]"
            />
            {{ item.status }}
          </div>
        </template>
        <template #tooltip>
          {{ item.failureReason }}
        </template>
      </Tooltip>
      <div v-else>
        <div
          class="inline-block w-2 h-2 mr-1 rounded-full"
          :class="paymentStatusToColorMap[item.status]"
        />
        {{ item.status }}
      </div>
    </template>

    <template #row(amount)="{ item }">
      <span>{{ item.currency + item.amount }}</span>
    </template>

    <template #row(date)="{ item }">
      <div
        :title="`Due Date: ${item.dueDate ? formatDate(item.dueDate, 'MMM dd, yyyy') : 'N/A'}
Date: ${item.date ? formatDate(item.date, 'MMM dd, yyyy') : 'N/A'}`"
      >
        <span v-if="item.date">{{ formatDate(item.date, 'MMM dd, yyyy') }}</span>
        <span v-else-if="item.dueDate">{{ formatDate(item.dueDate, 'MMM dd, yyyy') }}</span>
        <span v-else>N/A</span>
      </div>
    </template>

    <template #action="{ item }">
      <TbDrawer>
        <template
          v-if="selectedPaymentSchedule"
          #default="{ toggleDrawer }"
        >
          <Table
            :table-id="`payments-schedule-${item.id}`"
            item-type="Payment"
            date-key="dueDate"
            :items="selectedPaymentSchedule"
            :total-item-count="selectedPaymentSchedule?.length"
            :headers="paymentsScheduleTableHeaders"
            :state="paymentsScheduleState"
            flat
          />

          <button
            class="btn btn-secondary btn-block"
            @click="toggleDrawer"
          >
            Close
          </button>
        </template>

        <template #toggle="{ toggleDrawer }">
          <button
            class="btn btn-block btn-sm btn-ghost text-left"
            @click="toggleDrawer(); loadPaymentSchedule(item.id)"
          >
            Schedule
          </button>
        </template>
      </TbDrawer>
    </template>
  </Table>
</template>

<script setup lang="ts">
import RoleControlledAction from '@/components/role_controlled_action.vue';
import Table from '@/components/table.vue';
import { TbDrawer } from '@/components/tasty_bistro';
import Tooltip from '@/components/tooltip.vue';
import { currencyInCents } from '@/filters/currency';
import { formatDate } from '@/filters/date';
import { paymentStatusToColorMap } from '@/helpers/category_color_mapper';
import { Pages } from '@/router/index';
import { PageState } from '@/types';
import {
  computed,
  inject,
  Ref,
  ref,
} from 'vue';
import { useRoute } from 'vue-router';
import { ProjectState } from '../../../api/get_project_state';
import { getPaymentsSchedule, Payment } from '../api/get_payments_schedule';
import { roleConfig } from './role_configurations';

const route = useRoute();
const projectState = inject<Ref<ProjectState>>('project') as Ref<ProjectState>;
const projectId = route.params.projectId as string;
const orderId = route.params.id as string;

const roleName = computed(() => projectState.value.currentUserRoleName);

const canEditPayments = computed(() => {
  const hasEditPaymentsEnabled = projectState.value.features.editScheduleEnabled;
  if (!hasEditPaymentsEnabled) {
    return false;
  }

  const hasEditablePayments = props.installments.some((installment) => installment.isEditable);
  if (!hasEditablePayments) {
    return false;
  }

  return props.origin === 'In-House';
});

interface Installments {
  id: string;
  date: Date | null;
  productName: string;
  type: string;
  amount: string;
  status: string;
  isEditable: boolean;
}

interface Props {
  origin: string;
  installments: Installments[];
}

const props = defineProps<Props>();
const state = ref<PageState>(PageState.loaded);
const paymentsScheduleState = ref<PageState>(PageState.loaded);
const selectedPaymentSchedule = ref<Payment[] | null>(null);

const tableHeaders = [
  {
    title: 'Date',
    key: 'date',
  },
  {
    title: 'Payment ID',
    key: 'id',
  },
  {
    title: 'Checkout',
    key: 'productName',
  },
  {
    title: 'Type',
    key: 'type',
  },
  {
    title: 'Amount',
    key: 'amount',
    class: 'currency',
  },
  {
    title: 'Status',
    key: 'status',
  },
];

const paymentsScheduleTableHeaders = [
  {
    title: 'Due Date',
    key: 'dueDate',
    formatter: (val: string) => formatDate(val, 'MMM dd, yyyy'),
  },
  {
    title: 'Amount',
    key: 'amountInCents',
    formatter: currencyInCents,
    class: 'currency',
  },
  {
    title: 'Status',
    key: 'status',
  },
];

const loadPaymentSchedule = async (paymentId: string) => {
  selectedPaymentSchedule.value = null;

  try {
    paymentsScheduleState.value = PageState.loading;
    selectedPaymentSchedule.value = await getPaymentsSchedule({ id: paymentId, projectId });
    paymentsScheduleState.value = PageState.loaded;
  } catch (error) {
    paymentsScheduleState.value = PageState.error;
  }
};

</script>
